import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Input,
  VStack,
  HStack,
  Text,
  Divider,
  RadioGroup,
  Radio,
  Checkbox,
  FormControl,
  FormLabel,
  Select,
  IconButton,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  SimpleGrid,
  Heading,
  Flex,
} from "@chakra-ui/react";
import {
  DashboardDataTable,
  useDashboardTableStyles,
} from "../utils/DashboardDataTable";
import { useToast } from "@chakra-ui/react";
import { hitBeApi } from "../../../api/api";
import { CloseIcon } from "@chakra-ui/icons";
import { DashboardActionShell } from "../utils/DashboardCategoryPanel";

const SplitwiseExpenses = ({ state }) => {
  const [expenses, setExpenses] = useState([]);
  const [newExpenseDesc, setNewExpenseDesc] = useState("");
  const [newExpenseAmount, setNewExpenseAmount] = useState("");
  const [splitType, setSplitType] = useState("equal"); // Default split type
  const [editingExpense, setEditingExpense] = useState(null);
  const [userAmounts, setUserAmounts] = useState({});
  const [
    selectedExpenseParticipantMembers,
    setSelectedExpenseParticipantMembers,
  ] = useState([]);
  const toast = useToast();

  const textColor = useColorModeValue("gray.700", "white");
  const amountColor = useColorModeValue("teal.600", "teal.300");
  const radioGroupBackground = useColorModeValue("gray.50", "gray.800");
  const radioHoverBackground = useColorModeValue("gray.100", "gray.700");
  const mutedLabel = useColorModeValue("gray.600", "gray.400");
  const formLabelColor = useColorModeValue("gray.700", "gray.200");
  const selectBgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.300", "gray.500");
  const focusBorderColor = useColorModeValue("blue.500", "blue.300");
  const focusBoxShadow = useColorModeValue(
    "0 0 0 1px blue.500",
    "0 0 0 1px blue.300"
  );
  const titleColor = useColorModeValue("teal.800", "teal.200");
  const memberRowCheckedBg = useColorModeValue("white", "gray.800");

  const tableStyles = useDashboardTableStyles();

  const currentGroup = state.currentGroup;

  const memberIdKey = useMemo(
    () =>
      (currentGroup?.members || [])
        .map((m) => String(m._id))
        .sort()
        .join(","),
    [currentGroup?.members]
  );

  useEffect(() => {
    async function fetchExpenses() {
      if (!currentGroup?._id) return;
      try {
        const response = await hitBeApi(
          `splitwise/groups/${currentGroup._id}/expenses`,
          "GET",
          null
        );
        setExpenses(response.data);
      } catch (error) {
        toast({
          title: "Error fetching expenses.",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
    fetchExpenses();
  }, [currentGroup?._id, toast]);

  const [selectedPayerMember, setSelectedPayerMember] = useState("");

  useEffect(() => {
    const members = currentGroup?.members || [];
    if (!members.length) return;
    const ids = members.map((m) => String(m._id));
    setSelectedPayerMember((prev) =>
      prev && ids.includes(String(prev)) ? prev : ids[0]
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- memberIdKey reflects member list changes
  }, [currentGroup?._id, memberIdKey]);

  /** Default: everyone included in the split when members change (skip while editing). */
  useEffect(() => {
    if (editingExpense) return;
    const members = currentGroup?.members || [];
    if (!members.length) {
      setSelectedExpenseParticipantMembers([]);
      return;
    }
    setSelectedExpenseParticipantMembers(members.map((m) => String(m._id)));
  }, [currentGroup?._id, currentGroup?.members, editingExpense]);

  const handleChange = (event) => {
    setSelectedPayerMember(event.target.value);
  };

  const handleUserAmountChange = (userId, value) => {
    const key = String(userId);
    setUserAmounts((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateAmountInput = () => {
    const raw = String(newExpenseAmount ?? "").trim();
    if (!raw) {
      toast({
        title: "Amount required",
        description: "Enter a total amount in rupees.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
      toast({
        title: "Invalid amount",
        description: "Use a positive number (e.g. 250 or 99.50).",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const validateSplit = () => {
    const totalAmount = Number(newExpenseAmount);
    const userTotal = Object.values(userAmounts).reduce(
      (acc, val) => acc + Number(val),
      0
    );

    if (splitType === "unequal" && userTotal !== totalAmount) {
      toast({
        title: "Unequal Split Error.",
        description: `The total contributions (${userTotal}) must equal the expense amount (${totalAmount}).`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (splitType === "percentage" && userTotal !== 100) {
      toast({
        title: "Percentage Split Error.",
        description: `The total percentage (${userTotal}%) must equal 100%.`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const handleMemberSelection = (userId) => {
    const id = String(userId);
    setSelectedExpenseParticipantMembers((prevSelected) => {
      const isSelected = prevSelected.some((x) => String(x) === id);
      if (isSelected) {
        handleUserAmountChange(id, 0);
      }
      return isSelected
        ? prevSelected.filter((x) => String(x) !== id)
        : [...prevSelected, id];
    });
  };

  const addExpense = async () => {
    if (!String(newExpenseDesc ?? "").trim()) {
      toast({
        title: "Validation Error.",
        description: "Please provide a description for the expense.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!validateAmountInput()) return;
    if (!selectedExpenseParticipantMembers.length) {
      toast({
        title: "Choose who splits",
        description: "Select at least one person in “Split between”.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!validateSplit()) return; // Validate split before proceeding

    const participants = selectedExpenseParticipantMembers
      ?.filter((userId) => String(userId) !== String(selectedPayerMember))
      .map((userId) => {
        const uid = String(userId);
        return {
          payer: uid,
          receiver: selectedPayerMember,
          sharedAmount:
            splitType === "percentage"
              ? (newExpenseAmount * (Number(userAmounts[uid] || 0) / 100)).toFixed(
                  2
                )
              : splitType === "unequal"
                ? userAmounts[uid]
                : (
                    newExpenseAmount / selectedExpenseParticipantMembers.length
                  ).toFixed(2),
        };
      });

    const newExpense = {
      description: newExpenseDesc,
      amount: Number(newExpenseAmount),
      splitType: splitType,
      paidBy: selectedPayerMember,
      participants: participants,
    };

    try {
      const response = await hitBeApi(
        `splitwise/groups/${currentGroup._id}/expenses/add`,
        "POST",
        newExpense
      );
      if (response.success) {
        setExpenses([...expenses, response.data]);
        toast({
          title: "Expense added.",
          description: "Your expense has been added successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setNewExpenseDesc("");
        setNewExpenseAmount("");
        setSplitType("equal");
        setUserAmounts({});
        setSelectedExpenseParticipantMembers([]);
      }
    } catch (error) {
      toast({
        title: "Error adding expense.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const editExpense = (expense) => {
    setEditingExpense(expense);
    setNewExpenseDesc(expense.description);
    setNewExpenseAmount(String(expense.amount ?? ""));
    setSplitType(expense.splitType);
    setSelectedPayerMember(
      expense?.paidBy?._id != null ? String(expense.paidBy._id) : ""
    );
    const ids = new Set();
    if (expense?.paidBy?._id) ids.add(String(expense.paidBy._id));
    (expense.participants || []).forEach((p) => {
      const id = p.payer?._id != null ? String(p.payer._id) : p.payer && String(p.payer);
      if (id) ids.add(id);
    });
    const memberList = currentGroup?.members || [];
    setSelectedExpenseParticipantMembers(
      ids.size > 0
        ? Array.from(ids)
        : memberList.map((m) => String(m._id))
    );
    const ua = {};
    (expense.participants || []).forEach((p) => {
      const id = p.payer?._id != null ? String(p.payer._id) : p.payer && String(p.payer);
      if (!id) return;
      if (expense.splitType === "percentage" || expense.splitType === "unequal") {
        ua[id] = String(p.sharedAmount ?? "");
      }
    });
    setUserAmounts(ua);
  };

  const updateExpense = async () => {
    if (!String(newExpenseDesc ?? "").trim()) {
      toast({
        title: "Validation Error.",
        description: "Please provide a description for the expense.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!validateAmountInput()) return;
    if (!selectedExpenseParticipantMembers.length) {
      toast({
        title: "Choose who splits",
        description: "Select at least one person in “Split between”.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!validateSplit()) return; // Validate split before updating

    const participants = selectedExpenseParticipantMembers
      ?.filter((userId) => String(userId) !== String(selectedPayerMember))
      .map((userId) => {
        const uid = String(userId);
        return {
          payer: uid,
          receiver: selectedPayerMember,
          sharedAmount:
            splitType === "percentage"
              ? (newExpenseAmount * (Number(userAmounts[uid] || 0) / 100)).toFixed(
                  2
                )
              : splitType === "unequal"
                ? userAmounts[uid]
                : newExpenseAmount / selectedExpenseParticipantMembers.length,
        };
      });
    const updatedExpense = {
      ...editingExpense,
      description: newExpenseDesc,
      amount: Number(newExpenseAmount),
      splitType: splitType,
      paidBy: selectedPayerMember,
      participants: participants,
    };

    try {
      const response = await hitBeApi(
        `splitwise/groups/${currentGroup._id}/expenses/${editingExpense._id}/update`,
        "PUT",
        updatedExpense
      );
      if (response.success) {
        setExpenses(
          expenses.map((exp) =>
            exp._id === editingExpense._id ? response.data : exp
          )
        );
        toast({
          title: "Expense updated.",
          description: "Expense has been updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setEditingExpense(null);
        setNewExpenseDesc("");
        setNewExpenseAmount("");
        setSplitType("equal");
        setUserAmounts({});
        setSelectedExpenseParticipantMembers([]);
      }
    } catch (error) {
      toast({
        title: "Error updating expense.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const deleteExpense = async (expenseId) => {
    try {
      await hitBeApi(
        `splitwise/groups/${currentGroup._id}/expenses/${expenseId}/delete`,
        "DELETE",
        null
      );
      setExpenses(expenses.filter((exp) => exp._id !== expenseId));
      toast({
        title: "Expense deleted.",
        description: "Expense has been deleted successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error deleting expense.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (!currentGroup?._id) {
    return null;
  }

  const members = currentGroup.members || [];

  return (
    <Box w="100%" position="relative">
      <Flex
        align={{ base: "flex-start", sm: "center" }}
        justify="space-between"
        gap={3}
        mb={5}
        flexWrap="wrap"
      >
        <Box>
          <Heading
            as="h2"
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="bold"
            color={titleColor}
          >
            {currentGroup.name}
          </Heading>
          <Text fontSize="sm" color={mutedLabel} mt={1}>
            Record expenses and split between members.
          </Text>
        </Box>
        <IconButton
          aria-label="Close expenses"
          icon={<CloseIcon />}
          size="sm"
          colorScheme="gray"
          variant="ghost"
          onClick={() => state.setShowModifyExpenses(false)}
        />
      </Flex>

      <VStack spacing={6} align="stretch" w="100%">
        <DashboardDataTable
          title="Recorded expenses"
          description={
            expenses.length === 0
              ? "No expenses yet — add one below."
              : `${expenses.length} expense${
                  expenses.length === 1 ? "" : "s"
                } in this group.`
          }
          maxH={{ base: "none", md: "min(45vh, 360px)" }}
        >
          <Table
            variant="simple"
            size="sm"
            colorScheme="gray"
            sx={{ borderCollapse: "separate", borderSpacing: 0 }}
          >
            <Thead
              position="sticky"
              top={0}
              zIndex={1}
              bg={tableStyles.tableHeaderBg}
              backdropFilter="blur(8px)"
              borderBottomWidth="1px"
              borderColor={tableStyles.borderColor}
            >
              <Tr>
                <Th {...tableStyles.thProps}>Description</Th>
                <Th {...tableStyles.thProps} isNumeric>
                  Amount
                </Th>
                <Th {...tableStyles.thProps} textAlign="right" w="140px">
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {expenses.map((expense) => (
                <Tr key={expense._id} sx={tableStyles.trHoverSx}>
                  <Td py={3} maxW={{ base: "140px", md: "320px" }}>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={textColor}
                      wordBreak="break-word"
                      noOfLines={3}
                    >
                      {expense.description}
                    </Text>
                  </Td>
                  <Td isNumeric py={3}>
                    <Text
                      fontWeight="bold"
                      color={amountColor}
                      fontVariantNumeric="tabular-nums"
                      fontSize="sm"
                    >
                      ₹{expense.amount}
                    </Text>
                  </Td>
                  <Td textAlign="right" py={3}>
                    <HStack spacing={2} justify="flex-end">
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => editExpense(expense)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => deleteExpense(expense._id)}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DashboardDataTable>

        <Divider />

        <DashboardActionShell
          kind="splitwise"
          badgeLabel={editingExpense ? "Edit" : "New"}
          title={editingExpense ? "Update expense" : "Add expense"}
          subtitle="Describe the bill, total ₹, who paid, and how it’s split."
        >
          <VStack spacing={5} align="stretch" w="100%">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
              <FormControl>
                <FormLabel fontSize="sm" color={formLabelColor} mb={1}>
                  Description
                </FormLabel>
                <Input
                  placeholder="What was it for?"
                  value={newExpenseDesc}
                  onChange={(e) => setNewExpenseDesc(e.target.value)}
                  size="md"
                  borderColor={borderColor}
                  _focus={{
                    borderColor: focusBorderColor,
                    boxShadow: focusBoxShadow,
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color={formLabelColor} mb={1}>
                  Total amount (₹)
                </FormLabel>
                <Input
                  placeholder="0.00"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={newExpenseAmount}
                  onChange={(e) => setNewExpenseAmount(e.target.value)}
                  size="md"
                  borderColor={borderColor}
                  fontVariantNumeric="tabular-nums"
                  _focus={{
                    borderColor: focusBorderColor,
                    boxShadow: focusBoxShadow,
                  }}
                />
              </FormControl>
            </SimpleGrid>

            <FormControl maxW={{ md: "50%" }}>
              <FormLabel fontSize="sm" color={formLabelColor} mb={1}>
                Paid by
              </FormLabel>
              <Select
                placeholder="Select who paid"
                value={selectedPayerMember}
                onChange={handleChange}
                bg={selectBgColor}
                size="md"
                borderColor={borderColor}
                _focus={{ borderColor: focusBorderColor, boxShadow: "outline" }}
              >
                {members.map((member) => (
                  <option key={String(member._id)} value={String(member._id)}>
                    {member.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <Box
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              overflow="hidden"
              bg={radioGroupBackground}
            >
              <Box px={3} py={2} borderBottomWidth="1px" borderColor={borderColor}>
                <Text fontSize="sm" fontWeight="semibold" color={formLabelColor}>
                  How to split
                </Text>
              </Box>
              <Box p={3}>
                <RadioGroup onChange={setSplitType} value={splitType} w="100%">
                  <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={2}>
                    <Radio value="equal" _hover={{ bg: radioHoverBackground }} p={2} rounded="md">
                      Equal
                    </Radio>
                    <Radio value="unequal" _hover={{ bg: radioHoverBackground }} p={2} rounded="md">
                      Exact amounts
                    </Radio>
                    <Radio value="percentage" _hover={{ bg: radioHoverBackground }} p={2} rounded="md">
                      Percentage
                    </Radio>
                  </SimpleGrid>
                </RadioGroup>
              </Box>
            </Box>

            <Box
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              overflow="hidden"
            >
              <Box
                px={4}
                py={3}
                bg={radioGroupBackground}
                borderBottomWidth="1px"
                borderColor={borderColor}
              >
                <Text fontSize="sm" fontWeight="semibold" color={formLabelColor}>
                  Split between
                </Text>
                <Text fontSize="xs" color={mutedLabel} mt={1}>
                  Include everyone who should share this expense. For equal split, the total is divided evenly.
                </Text>
              </Box>
              <Box
                maxH={{ base: "min(52vh, 280px)", md: "260px" }}
                overflowY="auto"
                px={3}
                py={3}
              >
                <VStack align="stretch" spacing={3}>
                  {members.map((member) => {
                    const mid = String(member._id);
                    const checked = selectedExpenseParticipantMembers.some(
                      (id) => String(id) === mid
                    );
                    const showAmount = splitType !== "equal";
                    return (
                      <Flex
                        key={mid}
                        align="center"
                        gap={3}
                        flexWrap={{ base: "wrap", sm: "nowrap" }}
                        py={2}
                        px={3}
                        borderRadius="md"
                        bg={checked ? memberRowCheckedBg : "transparent"}
                        borderWidth="1px"
                        borderColor={checked ? focusBorderColor : borderColor}
                        minH="56px"
                        transition="border-color 0.15s ease"
                      >
                        <Checkbox
                          isChecked={checked}
                          onChange={() => handleMemberSelection(mid)}
                          colorScheme="teal"
                          flex="1"
                          minW="140px"
                        >
                          <Text fontSize="sm" fontWeight="medium">
                            {member.name}
                          </Text>
                        </Checkbox>
                        <Box flexShrink={0} w={{ base: "100%", sm: "112px" }} textAlign="right">
                          {showAmount ? (
                            <Input
                              size="sm"
                              placeholder={splitType === "percentage" ? "%" : "₹"}
                              value={userAmounts[mid] || ""}
                              onChange={(e) =>
                                handleUserAmountChange(mid, e.target.value)
                              }
                              isDisabled={!checked}
                              borderColor={borderColor}
                              fontVariantNumeric="tabular-nums"
                              textAlign="right"
                            />
                          ) : (
                            <Text fontSize="xs" color={mutedLabel} py={2}>
                              {checked ? "Split evenly" : "—"}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>
            </Box>

            <Button
              w="100%"
              colorScheme="teal"
              size="lg"
              rounded="lg"
              onClick={editingExpense ? updateExpense : addExpense}
            >
              {editingExpense ? "Save changes" : "Add expense"}
            </Button>
          </VStack>
        </DashboardActionShell>
      </VStack>
    </Box>
  );
};

export default SplitwiseExpenses;
